// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface WallNotification {
  id: string;
  recipient_id: string;
  actor_id: string;
  post_id: string;
  comment_id: string | null;
  type: 'like' | 'comment' | 'mention';
  is_read: boolean;
  created_at: string;
  actor: {
    id: string;
    name: string | null;
    avatar_url: string | null;
  };
}

export const useWallNotifications = (profileId?: string) => {
  const queryClient = useQueryClient();

  const {
    data: notifications = [],
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['wall_notifications', profileId],
    queryFn: async () => {
      if (!profileId) return [];

      const { data, error } = await supabase
        .from('wall_notifications')
        .select(`
          *,
          actor:actor_id (
            id,
            name,
            avatar_url
          )
        `)
        .eq('recipient_id', profileId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      
      return (data || []).map(n => ({
        ...n,
        actor: Array.isArray(n.actor) ? n.actor[0] : n.actor
      })) as WallNotification[];
    },
    enabled: !!profileId,
    refetchInterval: 30000, // Poll every 30 seconds
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('wall_notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wall_notifications', profileId] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!profileId) return;

      const { error } = await supabase
        .from('wall_notifications')
        .update({ is_read: true })
        .eq('recipient_id', profileId)
        .eq('is_read', false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wall_notifications', profileId] });
    },
  });

  // Create notification helper (used by like/comment actions)
  const createNotification = async (data: {
    recipientId: string;
    actorId: string;
    postId: string;
    commentId?: string;
    type: 'like' | 'comment';
  }) => {
    // Don't notify yourself
    if (data.recipientId === data.actorId) return;

    const { error } = await supabase
      .from('wall_notifications')
      .insert({
        recipient_id: data.recipientId,
        actor_id: data.actorId,
        post_id: data.postId,
        comment_id: data.commentId || null,
        type: data.type,
      });

    if (error) console.error('Failed to create notification:', error);
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    refetch,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    createNotification,
  };
};
